import { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { PlusIcon, PencilIcon, TrashIcon, ExclamationTriangleIcon, HomeModernIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { formatCurrency } from '../../utils/formatters';
import { calculateEquity, calculateLTV } from '../../utils/mortgageCalculations';
import PropertyModal from './PropertyModal';

const Properties = () => {
  const navigate = useNavigate();
  const { properties, mortgages, deleteProperty } = useData();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);

  const handleEdit = (e, property) => {
    e.stopPropagation();
    setEditingProperty(property);
    setIsModalVisible(true);
  };

  const handleDelete = () => {
    if (propertyToDelete) {
      deleteProperty(propertyToDelete.id);
      setDeleteDialogOpen(false);
      setPropertyToDelete(null);
    }
  };

  const handleModalClose = () => {
    setEditingProperty(null);
    setIsModalVisible(false);
  };

  const getLinkedMortgage = (property) => {
    return mortgages.find(m => m.id === property.linkedMortgageId);
  };

  const totalPropertyValue = properties.reduce((sum, p) => sum + (p.currentValue || 0), 0);
  const totalMortgageBalance = properties.reduce((sum, p) => {
    const mortgage = getLinkedMortgage(p);
    return sum + (mortgage?.currentBalance || 0);
  }, 0);
  const totalEquity = totalPropertyValue - totalMortgageBalance;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold m-0">Properties</h3>
        <button
          onClick={() => setIsModalVisible(true)}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium transition-colors shadow-sm"
          style={{ backgroundColor: '#012E58' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
        >
          <PlusIcon className="w-4 h-4" />
          <span>Add Property</span>
        </button>
      </div>

      {/* Summary Cards */}
      {properties.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Property Value</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(totalPropertyValue, 'GBP')}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Mortgage Balance</p>
            <p className="text-xl font-bold text-red-600 mt-1">{formatCurrency(totalMortgageBalance, 'GBP')}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Equity</p>
            <p className="text-xl font-bold text-green-600 mt-1">{formatCurrency(totalEquity, 'GBP')}</p>
          </div>
        </div>
      )}

      {properties.length === 0 ? (
        <div className="mt-6">
          <div className="flex flex-col items-center gap-4">
            <HomeModernIcon className="w-12 h-12 text-gray-300" />
            <p className="text-gray-600">No properties added yet</p>
            <button
              onClick={() => setIsModalVisible(true)}
              className="px-4 py-2 text-white rounded-lg font-medium transition-colors shadow-sm"
              style={{ backgroundColor: '#012E58' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
            >
              Add Your First Property
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {properties.map(property => {
            const mortgage = getLinkedMortgage(property);
            const equity = calculateEquity(property.currentValue, mortgage?.currentBalance || 0);
            const ltv = mortgage ? calculateLTV(mortgage.currentBalance, property.currentValue) : 0;
            const gain = property.purchasePrice > 0 ? property.currentValue - property.purchasePrice : 0;
            const gainPercent = property.purchasePrice > 0 ? ((property.currentValue - property.purchasePrice) / property.purchasePrice) * 100 : 0;

            return (
              <div
                key={property.id}
                onClick={() => navigate(`/properties/${property.id}`)}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-gray-900 truncate">{property.name}</h4>
                    {property.address && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">{property.address}</p>
                    )}
                  </div>
                  <div className="flex gap-1 ml-2">
                    <button onClick={(e) => handleEdit(e, property)} className="p-1 text-gray-400 hover:text-gray-600" title="Edit">
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setPropertyToDelete(property); setDeleteDialogOpen(true); }} className="p-1 text-gray-400 hover:text-red-600" title="Delete">
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Value</span>
                    <span className="text-sm font-bold text-gray-900">{formatCurrency(property.currentValue, property.currency)}</span>
                  </div>
                  {mortgage && (
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">Mortgage</span>
                      <span className="text-sm font-medium text-red-600">-{formatCurrency(mortgage.currentBalance, property.currency)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center border-t border-gray-100 pt-2">
                    <span className="text-xs text-gray-500">Equity</span>
                    <span className="text-sm font-bold text-green-600">{formatCurrency(equity, property.currency)}</span>
                  </div>
                  {mortgage && (
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">LTV</span>
                      <span className="text-xs font-medium text-gray-700">{ltv.toFixed(1)}%</span>
                    </div>
                  )}
                  {property.purchasePrice > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">Gain since purchase</span>
                      <span className={`text-xs font-medium ${gain >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {gain >= 0 ? '+' : ''}{formatCurrency(gain, property.currency)} ({gainPercent >= 0 ? '+' : ''}{gainPercent.toFixed(1)}%)
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-3 pt-2 border-t border-gray-100 flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full capitalize">{property.propertyType}</span>
                  <span className="text-xs text-gray-400">{property.annualGrowthRate}% growth</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <PropertyModal visible={isModalVisible} onCancel={handleModalClose} property={editingProperty} />

      {/* Delete confirmation dialog */}
      <Transition appear show={deleteDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setDeleteDialogOpen(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-sm transform rounded-2xl bg-white p-6 text-left shadow-xl transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                    <Dialog.Title as="h3" className="text-base font-semibold text-gray-900">Delete Property</Dialog.Title>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Are you sure you want to delete "{propertyToDelete?.name}"? This will unlink any associated mortgage but not delete it.
                  </p>
                  <div className="flex gap-3 justify-end">
                    <button onClick={() => setDeleteDialogOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                    <button onClick={handleDelete} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">Delete</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default Properties;
